// Light / dark mode for the member app.
//
// WHERE THE MODE COMES FROM
//   1. The membership OWNER picks the default in the admin (Brand → Theme),
//      stored in funnel_membership_settings.brand_settings.theme_mode and
//      delivered to this app by the same native `getfunnel` call that carries
//      the brand palette — see useBrand.js.
//   2. If the owner also enabled `theme_mode_switch`, members get a control in
//      the header and their choice (per membership, in localStorage) wins.
//
// The OS `prefers-color-scheme` never decides: a membership is the creator's
// branded product, so its default look is theirs to set, not the visitor's.
//
// WHAT APPLYING IT DOES
//   `<html data-theme="dark">` — every neutral utility in the app resolves to a
//   variable keyed off that attribute (see the note at the top of
//   tailwind.config.js), so the one attribute recolours everything.

import { useEffect, useState } from "react";
import { refreshCommunityTheme } from "./applyFont";

export const MODES = ["light", "dark"];

/** The mode currently painted on <html> — what a surface is actually rendering
 *  right now, as opposed to what a brand's settings resolve to. */
export function paintedMode() {
  if (typeof document === "undefined") return "light";
  return normalizeMode(document.documentElement.getAttribute("data-theme"));
}

/** Normalises anything (undefined, "Dark", 1, null) to a real mode. */
export function normalizeMode(mode) {
  const m = String(mode || "").toLowerCase();
  return MODES.includes(m) ? m : "light";
}

/** Did the owner allow members to choose? Accepts 1/"1"/true. */
export function themeSwitchEnabled(brand) {
  const v = brand?.theme_mode_switch;
  return v === 1 || v === "1" || v === true;
}

const overrideKey = (token) => `theme:${token}`;

/** The member's own choice for this membership, or null if they've made none. */
export function readOverride(token) {
  if (!token) return null;
  try {
    const v = localStorage.getItem(overrideKey(token));
    return MODES.includes(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeOverride(token, mode) {
  if (!token) return;
  try {
    localStorage.setItem(overrideKey(token), normalizeMode(mode));
  } catch {
    /* private mode — the choice just won't survive the reload */
  }
}

export function clearOverride(token) {
  if (!token) return;
  try {
    localStorage.removeItem(overrideKey(token));
  } catch {
    /* ignore */
  }
}

/**
 * The mode to render: the member's choice when the owner allows one, else the
 * owner's default. A stale override (member chose dark, owner later revoked the
 * switch) is ignored rather than deleted — re-enabling the switch restores the
 * member's preference instead of silently losing it.
 */
export function resolveThemeMode(brand, token) {
  if (themeSwitchEnabled(brand)) {
    const own = readOverride(token);
    if (own) return own;
  }
  return normalizeMode(brand?.theme_mode);
}

/**
 * Paints the mode. `color-scheme` is set alongside the attribute so the
 * browser's own chrome — form controls, scrollbars, the flash between paints —
 * matches; without it a dark page keeps white scrollbars.
 *
 * Also mirrors the mode into the boot cache the inline script in
 * public/index.html reads, so the NEXT load starts in the right mode instead of
 * flashing light until this call runs.
 */
export function applyThemeMode(mode, token) {
  const m = normalizeMode(mode);
  const root = document.documentElement;
  if (root.getAttribute("data-theme") !== m) {
    root.setAttribute("data-theme", m);
  }
  root.style.colorScheme = m;
  // The neutral ramps re-point themselves off the attribute, but the brand
  // vars are computed per mode (a dark brand colour has to be lifted to stay
  // visible on the dark surface), so they need recomputing on every flip.
  refreshCommunityTheme();
  if (token) {
    try {
      localStorage.setItem(`theme-boot:${token}`, m);
    } catch {
      /* ignore */
    }
  }
  return m;
}

/* ------------------------------------------------------------------ *
 * Live switching
 * ------------------------------------------------------------------ *
 * The mode is global page state, but the app has no theme provider — every
 * surface reads its branding through its own useBrand() call. So the header's
 * switch publishes here and anything showing the current mode subscribes,
 * instead of the mode being threaded through props from the app root.
 * ------------------------------------------------------------------ */
const listeners = new Set();

/** The member picked a mode: remember it, paint it, tell every listener. */
export function setThemeMode(token, mode) {
  const m = normalizeMode(mode);
  writeOverride(token, m);
  applyThemeMode(m, token);
  listeners.forEach((fn) => fn(m));
  return m;
}

export function subscribeThemeMode(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * `[mode, setMode]` for a component that both shows and can change the theme.
 *
 * `brand` is what useBrand() returned; it arrives empty on the first render and
 * fills in when getfunnel lands, which is why the owner's default is re-applied
 * whenever it changes. A member's own choice still wins inside resolveThemeMode.
 */
export function useThemeMode(token, brand) {
  const [mode, setMode] = useState(() => resolveThemeMode(brand, token));

  useEffect(() => subscribeThemeMode(setMode), []);

  useEffect(() => {
    const m = resolveThemeMode(brand, token);
    applyThemeMode(m, token);
    setMode(m);
  }, [brand, token]);

  return [mode, (next) => setThemeMode(token, next)];
}
